import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import styles from './initials-icon.module.css'

InitialsIcon.propTypes = {
    border: PropTypes.bool,
    children: PropTypes.string.isRequired,
    iconClassName: PropTypes.string,
    size: PropTypes.oneOf(['S', 'M', 'L', 'XL']),
    textClassName: PropTypes.string
}

InitialsIcon.defaultProps = {
    border: false,
    iconClassName: '',
    size: 'L',
    textClassName: ''
}

const red = ['A', 'Ä', 'À', 'Á', 'Â', 'Ã', 'Å', 'Æ', 'F', 'K', 'Ķ', 'P', 'Q', 'Z', 'Ź', 'Ż', 'Ž']
const aquamarine = [
    'B',
    'G',
    'Ĝ',
    'Ğ',
    'Ġ',
    'Ģ',
    'L',
    'Ĺ',
    'Ļ',
    'Ľ',
    'Ł',
    'U',
    'Ü',
    'Ù',
    'Ú',
    'Û',
    'Ũ',
    'Ū',
    'Ŭ',
    'Ů',
    'Ű',
    'Ų',
    'V'
]
const green = ['C', 'Ç', 'Ć', 'Ĉ', 'Ċ', 'H', 'Ĥ', 'Ħ', 'M', 'R', 'Ŕ', 'Ŗ', 'Ř', 'W', 'Ŵ']
const yellow = [
    'D',
    'Ď',
    'Đ',
    'I',
    'Ì',
    'Í',
    'Î',
    'Ï',
    'Ĩ',
    'Ī',
    'Ĭ',
    'Į',
    'İ',
    'N',
    'Ñ',
    'Ń',
    'Ņ',
    'Ň',
    'Ŋ',
    'S',
    'Ś',
    'Ŝ',
    'Ş',
    'Š',
    'X'
]
const blue = [
    'E',
    'È',
    'É',
    'Ê',
    'Ë',
    'Ē',
    'Ĕ',
    'Ė',
    'Ę',
    'Ě',
    'J',
    'Ĵ',
    'O',
    'Ö',
    'Ò',
    'Ó',
    'Ô',
    'Õ',
    'Ō',
    'Ŏ',
    'Ő',
    'Œ',
    'T',
    'Ţ',
    'Ť',
    'Ŧ',
    'Y',
    'Ý',
    'Ŷ',
    'Ÿ'
]

/**
 * A colored circular icon with one or two characters that are derived from the provided string (children).
 * If the string contains one word, the icon will contain only one character. If it contains more than one word, the
 * initial characters of the first and the last word will be used. Letters will be shown capitalized. The circular
 * background will be colored in regard to the first character of the string. There are optional parameters to customize
 * the icon, text and border. Empty strings or strings that consist of whitespace only will return an empty icon.
 *
 * @param {Object} props
 * @param {ReactElement|String} props.children - An element the initials will derive from (e.g. a provided string or
 *   the innerText of the custom tags.
 * @param {string} [props.iconClassName=''] - A custom CSS class name for the outer Circle element.
 * @param {string} [props.textClassName=''] - A custom CSS class name for the icons text.
 * @param {boolean} [props.border=false] - Custom CSS for the icons border.
 * @param {string} [props.size='L'] - The relative size of the icon and its text ('S', 'M', 'L', 'XL').
 * @param {Object} otherProps - All the other properties passed to that component.
 * @returns {JSX.Element|Fragment} - An initials icon (or an empty icon if props.children is not a valid string).
 *
 * @example
 *     <InitialsIcon>
 *        Arya Stark
 *     </InitialsIcon>
 */
// TODO: As soon as this ticket https://jira.ttt-sp.com/browse/ATLUI-1660 is done, otherProps are not needed anymore.
function InitialsIcon({ border, children, iconClassName, size, textClassName, ...otherProps }) {
    const childrenValidString = children && typeof children === 'string' && children.trim()
    const iconSize = styles['icon-' + size]
    const baseIconClassName = cx(styles['circle'], iconSize, { [styles['border']]: border }, iconClassName)

    if (!childrenValidString) {
        const combinedEmptyIconClassName = cx(baseIconClassName, getColor(), 't3sel-initials-icon-empty')

        return <div className={combinedEmptyIconClassName} {...otherProps} />
    }

    const words = childrenValidString.split(/\s/g).filter(word => word.length)
    const initials = words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0][0]
    const textSize = styles['text-' + size]

    const combinedIconClassName = cx(baseIconClassName, getColor(initials), 't3sel-initials-icon')

    const combinedTextClassName = cx(styles['initials'], textSize, textClassName, 't3sel-initials-icon-text')

    return (
        <div className={combinedIconClassName} {...otherProps}>
            <div className={combinedTextClassName}>{initials}</div>
        </div>
    )
}

/**
 * Uses the first character of the initials to return the corresponding CSS style which will be applied to the icons.
 * The style will set the icons background color. The character-to-color-mapping is predefined and fixed within this
 * component.
 *
 * @param {string} [initials] - The initials of the icon.
 * @returns {*} - The CSS style that will be applied to the icon to set its background color.
 */
function getColor(initials) {
    const firstLetter = initials && initials.length > 0 && initials[0].toUpperCase()
    if (red.indexOf(firstLetter) !== -1) {
        return styles['red']
    }

    if (aquamarine.indexOf(firstLetter) !== -1) {
        return styles['aquamarine']
    }

    if (green.indexOf(firstLetter) !== -1) {
        return styles['green']
    }

    if (yellow.indexOf(firstLetter) !== -1) {
        return styles['yellow']
    }

    if (blue.indexOf(firstLetter) !== -1) {
        return styles['blue']
    }

    return styles['blue']
}

export default InitialsIcon
